<template>
  <div class="ab-tests-page">
    <CommonOverlay :show="loading" title="Loading A/B Tests" message="Please wait..." />

    <!-- Page Header -->
    <div class="ab-tests-header">
      <div class="header-left">
        <h1 class="page-title">A/B Tests</h1>
        <p class="page-subtitle">Test different versions of your campaigns to optimize performance</p>
      </div>
      <div class="header-actions">
        <b-button variant="success" @click="openCreateTestModal">
          <i class="fa fa-plus mr-2"></i> Create A/B Test
        </b-button>
      </div>
    </div>

    <!-- Sub-navigation for Campaigns section -->
    <div class="campaigns-subnav">
      <router-link to="/app/campaigns" class="subnav-link">
        <i class="fa fa-bullhorn"></i>
        <span>Campaigns</span>
      </router-link>
      <router-link to="/app/campaigns-drafts" class="subnav-link">
        <i class="fa fa-pencil-square-o"></i>
        <span>Drafts</span>
      </router-link>
      <router-link to="/app/campaigns-statistics" class="subnav-link">
        <i class="fa fa-bar-chart"></i>
        <span>Reports</span>
      </router-link>
      <router-link to="/app/campaigns-templates" class="subnav-link">
        <i class="fa fa-file-text-o"></i>
        <span>Templates</span>
      </router-link>
      <router-link
        to="/app/campaigns-ab-tests"
        class="subnav-link"
        :class="{ active: $route.path === '/app/campaigns-ab-tests' }"
      >
        <i class="fa fa-flask"></i>
        <span>A/B Tests</span>
      </router-link>
    </div>

    <!-- Search and Filters -->
    <div class="ab-tests-toolbar">
      <div class="search-box">
        <i class="fa fa-search"></i>
        <input
          type="text"
          v-model="searchQuery"
          placeholder="Search A/B tests..."
          class="search-input"
        />
      </div>

      <div class="filter-group">
        <b-form-select v-model="filterStatus" :options="statusOptions" class="filter-select"></b-form-select>
      </div>

      <div class="filter-group">
        <b-form-select v-model="filterList" :options="listOptions" class="filter-select"></b-form-select>
      </div>
    </div>

    <!-- Empty State -->
    <div v-if="!loading && filteredTests.length === 0" class="empty-state">
      <div class="empty-illustration">
        <svg viewBox="0 0 200 200" class="illustration-svg">
          <circle cx="100" cy="100" r="80" fill="#f0f4f8" />
          <text x="70" y="90" font-size="40" fill="#667eea">A</text>
          <text x="110" y="120" font-size="40" fill="#764ba2">B</text>
          <line x1="90" y1="100" x2="110" y2="100" stroke="#dee2e6" stroke-width="3" />
        </svg>
      </div>
      <h4>No A/B Tests Found</h4>
      <p v-if="searchQuery">Try adjusting your search or filters</p>
      <p v-else>Create your first A/B test to optimize your campaign performance</p>
      <b-button v-if="!searchQuery" variant="primary" @click="openCreateTestModal">
        <i class="fa fa-plus mr-2"></i> Create A/B Test
      </b-button>
    </div>

    <!-- A/B Tests List -->
    <div v-if="filteredTests.length > 0" class="ab-tests-list">
      <div
        v-for="test in paginatedTests"
        :key="test.AbTestID"
        class="ab-test-card"
        @click="viewTestDetail(test)"
      >
        <div class="test-header">
          <div class="test-info">
            <h5 class="test-name">{{ test.Name }}</h5>
            <div class="test-meta">
              <span class="test-type">
                <i :class="getTestTypeIcon(test.Type)"></i>
                {{ getTestTypeLabel(test.Type) }}
              </span>
              <span class="test-date">Created {{ test.CreatedAt | relativeDate }}</span>
            </div>
          </div>
          <div class="test-status">
            <b-badge :variant="getStatusVariant(test.Status)">{{ test.Status }}</b-badge>
          </div>
        </div>

        <div class="test-variants">
          <!-- Variant A -->
          <div class="variant-card variant-a">
            <div class="variant-header">
              <span class="variant-label">Variant A</span>
              <span v-if="test.Winner === 'A'" class="winner-badge">
                <i class="fa fa-trophy"></i> Winner
              </span>
            </div>
            <div class="variant-content">
              <p class="variant-subject" v-if="test.VariantA.subject">
                "{{ test.VariantA.subject }}"
              </p>
            </div>
            <div class="variant-stats">
              <div class="stat">
                <span class="stat-value">{{ test.VariantA.sent }}</span>
                <span class="stat-label">Sent</span>
              </div>
              <div class="stat">
                <span class="stat-value">{{ test.VariantA.openRate }}%</span>
                <span class="stat-label">Open Rate</span>
              </div>
              <div class="stat">
                <span class="stat-value">{{ test.VariantA.clickRate }}%</span>
                <span class="stat-label">Click Rate</span>
              </div>
            </div>
          </div>

          <!-- VS Divider -->
          <div class="vs-divider">
            <span>VS</span>
          </div>

          <!-- Variant B -->
          <div class="variant-card variant-b">
            <div class="variant-header">
              <span class="variant-label">Variant B</span>
              <span v-if="test.Winner === 'B'" class="winner-badge">
                <i class="fa fa-trophy"></i> Winner
              </span>
            </div>
            <div class="variant-content">
              <p class="variant-subject" v-if="test.VariantB.subject">
                "{{ test.VariantB.subject }}"
              </p>
            </div>
            <div class="variant-stats">
              <div class="stat">
                <span class="stat-value">{{ test.VariantB.sent }}</span>
                <span class="stat-label">Sent</span>
              </div>
              <div class="stat">
                <span class="stat-value">{{ test.VariantB.openRate }}%</span>
                <span class="stat-label">Open Rate</span>
              </div>
              <div class="stat">
                <span class="stat-value">{{ test.VariantB.clickRate }}%</span>
                <span class="stat-label">Click Rate</span>
              </div>
            </div>
          </div>
        </div>

        <div class="test-footer">
          <div class="test-settings">
            <span class="setting">
              <i class="fa fa-clock-o"></i> {{ test.TestDuration }}
            </span>
            <span class="setting">
              <i class="fa fa-trophy"></i> Winner by {{ test.WinnerCriteria }}
            </span>
          </div>
          <div class="test-actions" @click.stop>
            <b-dropdown variant="link" no-caret right toggle-class="action-btn">
              <template #button-content>
                <i class="fa fa-ellipsis-h"></i>
              </template>
              <b-dropdown-item @click="viewTestDetail(test)">
                <i class="fa fa-eye mr-2"></i> View Results
              </b-dropdown-item>
              <b-dropdown-item v-if="test.Status === 'Draft'" @click="editTest(test)">
                <i class="fa fa-pencil mr-2"></i> Edit
              </b-dropdown-item>
              <b-dropdown-item v-if="test.Status === 'Draft'" @click="startTest(test)">
                <i class="fa fa-play mr-2"></i> Start Test
              </b-dropdown-item>
              <b-dropdown-item v-if="test.Status === 'Running'" @click="stopTest(test)">
                <i class="fa fa-stop mr-2"></i> Stop Test
              </b-dropdown-item>
              <b-dropdown-item v-if="test.Winner" @click="sendWinnerToRemaining(test)">
                <i class="fa fa-paper-plane mr-2"></i> Send Winner to All
              </b-dropdown-item>
              <b-dropdown-divider></b-dropdown-divider>
              <b-dropdown-item @click="duplicateTest(test)">
                <i class="fa fa-copy mr-2"></i> Duplicate
              </b-dropdown-item>
              <b-dropdown-item @click="confirmDeleteTest(test)" class="text-danger">
                <i class="fa fa-trash mr-2"></i> Delete
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div class="pagination-container" v-if="totalPages > 1">
      <div class="pagination-info">
        Showing {{ startItem }}-{{ endItem }} of {{ filteredTests.length }}
      </div>
      <div class="pagination-controls">
        <button :disabled="currentPage === 1" @click="currentPage--">
          <i class="fa fa-chevron-left"></i>
        </button>
        <span>Page {{ currentPage }} of {{ totalPages }}</span>
        <button :disabled="currentPage === totalPages" @click="currentPage++">
          <i class="fa fa-chevron-right"></i>
        </button>
      </div>
    </div>

    <!-- Create A/B Test Modal -->
    <CreateABTestModal
      ref="createTestModal"
      :test="selectedTest"
      :is-editing="isEditing"
      @saved="onTestSaved"
    />

    <!-- Delete Confirmation Modal -->
    <b-modal
      ref="deleteModal"
      title="Delete A/B Test"
      centered
      @ok="deleteTest"
    >
      <p>Are you sure you want to delete the A/B test <strong>"{{ testToDelete?.Name }}"</strong>?</p>
      <p class="text-danger mb-0">This action cannot be undone.</p>

      <template #modal-footer="{ ok, cancel }">
        <b-button variant="outline-secondary" @click="cancel()">Cancel</b-button>
        <b-button variant="danger" @click="ok()">Delete</b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
/* eslint-disable */
import CommonOverlay from '@/components/CommonOverlay';
import CreateABTestModal from './components/CreateABTestModal.vue';

export default {
  name: 'CampaignABTests',

  components: {
    CommonOverlay,
    CreateABTestModal
  },

  data() {
    return {
      loading: false,
      searchQuery: '',
      filterStatus: 'all',
      filterList: null,
      currentPage: 1,
      perPage: 10,
      selectedTest: null,
      isEditing: false,
      testToDelete: null,

      statusOptions: [
        { value: 'all', text: 'All Status' },
        { value: 'Draft', text: 'Draft' },
        { value: 'Running', text: 'Running' },
        { value: 'Completed', text: 'Completed' }
      ],

      listOptions: [
        { value: null, text: 'All Lists' },
        { value: 'list1', text: 'Active Patients' },
        { value: 'list2', text: 'Recall Due' }
      ],

      // Mock A/B tests data
      abTests: [
        {
          AbTestID: '1',
          Name: 'Subject Line Test - Recall Campaign',
          Status: 'Running',
          Type: 'SubjectLine',
          CreatedAt: '2024-03-15T10:00:00Z',
          VariantA: {
            subject: 'Time for your dental checkup!',
            sent: 500,
            opened: 234,
            clicked: 45,
            openRate: 46.8,
            clickRate: 9.0
          },
          VariantB: {
            subject: '{{FirstName}}, your smile needs attention',
            sent: 500,
            opened: 289,
            clicked: 67,
            openRate: 57.8,
            clickRate: 13.4
          },
          Winner: null,
          WinnerCriteria: 'Open Rate',
          TestDuration: '24h'
        },
        {
          AbTestID: '2',
          Name: 'Welcome Email Content Test',
          Status: 'Completed',
          Type: 'Content',
          CreatedAt: '2024-03-01T09:00:00Z',
          VariantA: {
            subject: 'Welcome to Our Practice!',
            sent: 300,
            opened: 198,
            clicked: 67,
            openRate: 66.0,
            clickRate: 22.3
          },
          VariantB: {
            subject: 'Welcome to Our Practice!',
            sent: 300,
            opened: 186,
            clicked: 89,
            openRate: 62.0,
            clickRate: 29.7
          },
          Winner: 'B',
          WinnerCriteria: 'Click Rate',
          TestDuration: '48h'
        },
        {
          AbTestID: '3',
          Name: 'Promo Email Subject Test',
          Status: 'Draft',
          Type: 'SubjectLine',
          CreatedAt: '2024-03-20T14:00:00Z',
          VariantA: {
            subject: '20% Off Whitening - Limited Time!',
            sent: 0,
            opened: 0,
            clicked: 0,
            openRate: 0,
            clickRate: 0
          },
          VariantB: {
            subject: 'Your Exclusive Whitening Discount Inside',
            sent: 0,
            opened: 0,
            clicked: 0,
            openRate: 0,
            clickRate: 0
          },
          Winner: null,
          WinnerCriteria: 'Open Rate',
          TestDuration: '24h'
        }
      ]
    };
  },

  computed: {
    filteredTests() {
      let filtered = [...this.abTests];

      // Search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(t => t.Name.toLowerCase().includes(query));
      }

      // Status filter
      if (this.filterStatus !== 'all') {
        filtered = filtered.filter(t => t.Status === this.filterStatus);
      }

      return filtered;
    },

    paginatedTests() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredTests.slice(start, end);
    },

    totalPages() {
      return Math.ceil(this.filteredTests.length / this.perPage);
    },

    startItem() {
      return (this.currentPage - 1) * this.perPage + 1;
    },

    endItem() {
      const end = this.currentPage * this.perPage;
      return end > this.filteredTests.length ? this.filteredTests.length : end;
    }
  },

  mounted() {
    this.loadTests();
  },

  methods: {
    async loadTests() {
      this.loading = true;
      try {
        // In production, fetch from Vuex store
        await new Promise(resolve => setTimeout(resolve, 500));
      } catch (error) {
        console.error('Error loading A/B tests:', error);
      } finally {
        this.loading = false;
      }
    },

    getTestTypeIcon(type) {
      return type === 'SubjectLine' ? 'fa fa-font' : 'fa fa-file-text';
    },

    getTestTypeLabel(type) {
      return type === 'SubjectLine' ? 'Subject Line Test' : 'Content Test';
    },

    getStatusVariant(status) {
      const variants = {
        'Draft': 'secondary',
        'Running': 'success',
        'Completed': 'info',
        'Paused': 'warning'
      };
      return variants[status] || 'secondary';
    },

    openCreateTestModal() {
      this.selectedTest = null;
      this.isEditing = false;
      this.$refs.createTestModal.show();
    },

    viewTestDetail(test) {
      // Navigate to detailed results page
      console.log('View test detail:', test.AbTestID);
      this.$toast?.info?.('A/B Test detail page coming soon');
    },

    editTest(test) {
      this.selectedTest = { ...test };
      this.isEditing = true;
      this.$refs.createTestModal.show();
    },

    startTest(test) {
      const index = this.abTests.findIndex(t => t.AbTestID === test.AbTestID);
      if (index > -1) {
        this.abTests[index].Status = 'Running';
        this.$toast?.success?.('A/B Test started');
      }
    },

    stopTest(test) {
      const index = this.abTests.findIndex(t => t.AbTestID === test.AbTestID);
      if (index > -1) {
        this.abTests[index].Status = 'Completed';
        // Determine winner based on criteria
        const a = this.abTests[index].VariantA;
        const b = this.abTests[index].VariantB;
        const criteria = this.abTests[index].WinnerCriteria;

        if (criteria === 'Open Rate') {
          this.abTests[index].Winner = a.openRate > b.openRate ? 'A' : 'B';
        } else {
          this.abTests[index].Winner = a.clickRate > b.clickRate ? 'A' : 'B';
        }
        this.$toast?.success?.('A/B Test completed');
      }
    },

    sendWinnerToRemaining(test) {
      this.$toast?.success?.(`Sending winner (Variant ${test.Winner}) to remaining recipients`);
    },

    duplicateTest(test) {
      const newTest = {
        ...test,
        AbTestID: Date.now().toString(),
        Name: `${test.Name} (Copy)`,
        Status: 'Draft',
        CreatedAt: new Date().toISOString(),
        VariantA: { ...test.VariantA, sent: 0, opened: 0, clicked: 0, openRate: 0, clickRate: 0 },
        VariantB: { ...test.VariantB, sent: 0, opened: 0, clicked: 0, openRate: 0, clickRate: 0 },
        Winner: null
      };
      this.abTests.unshift(newTest);
      this.$toast?.success?.('A/B Test duplicated');
    },

    confirmDeleteTest(test) {
      this.testToDelete = test;
      this.$refs.deleteModal.show();
    },

    deleteTest() {
      if (!this.testToDelete) return;

      const index = this.abTests.findIndex(t => t.AbTestID === this.testToDelete.AbTestID);
      if (index > -1) {
        this.abTests.splice(index, 1);
        this.$toast?.success?.('A/B Test deleted');
      }
      this.$refs.deleteModal.hide();
      this.testToDelete = null;
    },

    onTestSaved(test) {
      if (this.isEditing) {
        const index = this.abTests.findIndex(t => t.AbTestID === test.AbTestID);
        if (index > -1) {
          this.abTests[index] = test;
        }
      } else {
        this.abTests.unshift(test);
      }
      this.$toast?.success?.(`A/B Test ${this.isEditing ? 'updated' : 'created'} successfully`);
    }
  },

  filters: {
    relativeDate(dateStr) {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      return date.toLocaleDateString();
    }
  }
};
</script>

<style lang="scss" scoped>
.ab-tests-page {
  padding: 20px;
  background: #f8f9fa;
  min-height: 100vh;
}

.ab-tests-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;

  .page-title {
    font-size: 24px;
    font-weight: 600;
    color: #212529;
    margin: 0;
  }

  .page-subtitle {
    color: #6c757d;
    margin: 4px 0 0;
    font-size: 14px;
  }
}

// Sub-navigation for Campaigns section
.campaigns-subnav {
  display: flex;
  gap: 4px;
  background: white;
  padding: 6px;
  border-radius: 12px;
  margin-bottom: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid #e2e8f0;
  overflow-x: auto;

  .subnav-link {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    border-radius: 8px;
    color: #64748b;
    text-decoration: none;
    font-weight: 500;
    font-size: 0.9rem;
    white-space: nowrap;
    transition: all 0.2s ease;

    i {
      font-size: 14px;
    }

    &:hover {
      background: #f1f5f9;
      color: #334155;
    }

    &.active,
    &.router-link-exact-active {
      background: linear-gradient(135deg, #3abf94, #2d9474);
      color: white;
      box-shadow: 0 2px 4px rgba(58, 191, 148, 0.3);
    }
  }

  @media (max-width: 576px) {
    .subnav-link {
      padding: 8px 12px;
      font-size: 0.85rem;

      span {
        display: none;
      }

      i {
        font-size: 16px;
      }
    }
  }
}

.ab-tests-toolbar {
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;

  .search-box {
    position: relative;
    flex: 1;
    min-width: 250px;
    max-width: 400px;

    .fa-search {
      position: absolute;
      left: 12px;
      top: 50%;
      transform: translateY(-50%);
      color: #adb5bd;
    }

    .search-input {
      width: 100%;
      padding: 10px 12px 10px 40px;
      border: 1px solid #dee2e6;
      border-radius: 8px;
      font-size: 14px;

      &:focus {
        outline: none;
        border-color: #007bff;
      }
    }
  }

  .filter-select {
    width: 150px;
    border-radius: 8px;
  }
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  background: #fff;
  border-radius: 12px;

  .empty-illustration {
    margin-bottom: 24px;

    .illustration-svg {
      width: 150px;
      height: 150px;
    }
  }

  h4 {
    color: #212529;
    margin-bottom: 8px;
  }

  p {
    color: #6c757d;
    margin-bottom: 16px;
  }
}

.ab-tests-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 24px;
}

.ab-test-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .test-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 16px;

    .test-name {
      font-size: 18px;
      font-weight: 600;
      color: #212529;
      margin: 0 0 8px;
    }

    .test-meta {
      display: flex;
      gap: 16px;
      font-size: 13px;
      color: #6c757d;

      .test-type {
        i {
          margin-right: 4px;
        }
      }
    }
  }

  .test-variants {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    gap: 16px;
    margin-bottom: 16px;
    align-items: stretch;

    .vs-divider {
      display: flex;
      align-items: center;
      justify-content: center;

      span {
        background: #e9ecef;
        color: #6c757d;
        font-weight: 600;
        font-size: 12px;
        padding: 8px 12px;
        border-radius: 20px;
      }
    }
  }

  .variant-card {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 16px;
    border: 2px solid transparent;

    &.variant-a {
      border-color: #667eea;
    }

    &.variant-b {
      border-color: #764ba2;
    }

    .variant-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;

      .variant-label {
        font-weight: 600;
        font-size: 13px;
        color: #6c757d;
        text-transform: uppercase;
      }

      .winner-badge {
        background: #ffc107;
        color: #212529;
        font-size: 11px;
        font-weight: 600;
        padding: 4px 8px;
        border-radius: 4px;

        i {
          margin-right: 4px;
        }
      }
    }

    .variant-content {
      margin-bottom: 12px;

      .variant-subject {
        font-size: 14px;
        color: #212529;
        margin: 0;
        font-style: italic;
      }
    }

    .variant-stats {
      display: flex;
      gap: 16px;

      .stat {
        text-align: center;

        .stat-value {
          display: block;
          font-size: 18px;
          font-weight: 600;
          color: #212529;
        }

        .stat-label {
          font-size: 11px;
          color: #6c757d;
          text-transform: uppercase;
        }
      }
    }
  }

  .test-footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 16px;
    border-top: 1px solid #e9ecef;

    .test-settings {
      display: flex;
      gap: 16px;

      .setting {
        font-size: 13px;
        color: #6c757d;

        i {
          margin-right: 4px;
        }
      }
    }

    .action-btn {
      padding: 4px 8px;
      color: #6c757d;

      &:hover {
        color: #212529;
      }
    }
  }
}

.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .pagination-info {
    font-size: 13px;
    color: #6c757d;
  }

  .pagination-controls {
    display: flex;
    align-items: center;
    gap: 12px;

    button {
      background: #fff;
      border: 1px solid #dee2e6;
      border-radius: 4px;
      padding: 6px 10px;
      cursor: pointer;

      &:hover:not(:disabled) {
        background: #f8f9fa;
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }
  }
}

@media (max-width: 768px) {
  .ab-test-card .test-variants {
    grid-template-columns: 1fr;

    .vs-divider {
      padding: 8px 0;
    }
  }
}
</style>
